// THE OPEN FILE, as state (Figma frames 60–64 / m60–m64; Pouria, 2026-10-15).
//
// Opening a stored document — from the Recent files page's View, a Work-board chip, an entity's
// attachments — puts the BOX into the "Briefing <file>" state (components/SmartBox/FileBriefingBox.vue),
// turns the middle into the file's conversation (flow G, S5) and keeps the full-window viewer
// (components/SmartBox/FileViewer.vue) one "Open" away. Three surfaces with no relationship to each
// other read the same record, so it lives here, as the Recent files page's state does.
//
// WHAT IS OPEN is still `workboard/preview` — the one slot the app has always kept for "the thing
// opened over the page" (the gutter's "✕ Close", the hidden Follow-ups, the Home launcher's reset
// all read it). A stored document in that slot carries its `documentId`, which is what
// lib/briefings/file.js's `isFileBriefing` answers on. This module holds everything ABOUT that
// document that the preview item does not: the resolved DocumentResponse, the tab, whether the
// viewer is up, the files the viewer's arrows step through, the page count the renderer reported,
// when it was opened, and the last question asked about it.
//
// ── HAND-OFF TO THE CONVERSATION (S5, flow G) ───────────────────────────────────────────────────
//   `fileBriefing/open`        → true while the box is in this state
//   `fileBriefing/documentId`  → the open document's id (the question's scope)
//   `fileBriefing/doc`         → its record
//   `fileBriefing/lastQuestion`→ `{ question, documentId, at }`, written by `ask` — the box's input,
//                                 the send button and a follow-up click all go through `ask`.
//
// ── WHERE IT WAS OPENED FROM (busy-my-web#644, Fatemeh 2026-10-16) ──────────────────────────────
//   `fileBriefing/openedFrom`  → `{ from: 'recent-files', page }` while the file was VIEWED from the
//                                 Recent files page (`page` is lib/recentFilesPage
//                                 `recentPageSnapshot`), else null. `close` reads it: Close on such a
//                                 file goes back to that page, as it was left — every other road in
//                                 closes to whatever the box was doing, as it always did. Named as
//                                 emailSession's `openedFrom` is, for the same job.

import api from '@/lib/services/general.service'
import { attachmentPreviewItem, documentObjectUrl, saveObjectUrl } from '@/lib/attachmentOpen'
import { fileChip, fileFacts, followUps, isFileBriefing, openableDocument, siblingOf, viewableDocuments } from '@/lib/briefings/file'

const closed = () => ({
  doc: null,
  tab: 'details',
  viewer: false,
  list: [],
  pages: null,
  openedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'ready' | 'error'
  lastQuestion: null,
  // Where the file was opened from, when Close must go back there (see the note at the top).
  openedFrom: null,
  // The Summary tab's content per document id (frame 61): { status: 'loading' | 'ready' | 'none' |
  // 'error', overview, facts: [{ label, value }] } — written once per file per session.
  summaries: {},
})

export const state = closed()

/** The BFF wraps replies as `{ data: { data } }` in some places and `{ data }` in others. */
const unwrap = (res) => res?.data?.data ?? res?.data ?? null

/** The story the box stands in, when it does — the only Ongoing name a document can be given. */
const storyOf = (rootGetters) => rootGetters['ongoing/currentStory'] || null

/** The preview item's own facts as a record, for the moment before the full one is in hand. */
const stubOf = (item) => ({ id: String(item.documentId), filename: String(item.title || ''), contentType: String(item.contentType || '') })

export const getters = {
  // WHAT IS OPEN is the preview slot (see the note at the top): a stored document in it is this
  // state, whether it was put there by `open` below or by any of the app's existing roads to
  // `workboard/openPreview` (an entity's attachment, a timeline row, a Work-board chip).
  open: (s, g, rootState, rootGetters) => isFileBriefing(rootGetters['workboard/preview']),
  doc: (s, g, rootState, rootGetters) => {
    const item = rootGetters['workboard/preview']
    if (!isFileBriefing(item)) return null
    return s.doc && String(s.doc.id) === String(item.documentId) ? s.doc : stubOf(item)
  },
  documentId: (s, g) => (g.doc ? String(g.doc.id || '') : ''),
  tab: s => s.tab,
  summary: (s, g) => (g.documentId && s.summaries[g.documentId]) || { status: 'idle', overview: '', facts: [] },
  viewerOpen: (s, g) => g.open && !!s.viewer,
  list: s => s.list,
  pages: s => s.pages,
  openedAt: s => s.openedAt,
  status: s => s.status,
  lastQuestion: s => s.lastQuestion,
  openedFrom: s => s.openedFrom,
  /** The viewer's arrows step through more than one DRAWABLE file. */
  canStep: s => viewableDocuments(s.list).length > 1,
  /** Is the open document on the Work-board (frame 63's "Selected")? */
  selected: (s, g, rootState, rootGetters) => !!g.documentId && !!rootGetters['workboard/has']?.(g.documentId),
  ongoingTitle: (s, g, rootState, rootGetters) => String(storyOf(rootGetters)?.title || '').trim(),
  ongoingId: (s, g, rootState, rootGetters) => String(storyOf(rootGetters)?.id || ''),
  facts: (s, g) => (g.doc ? fileFacts(g.doc, { pages: s.pages, ongoing: g.ongoingTitle, openedAt: s.openedAt }) : { left: [], right: [] }),
  followUps: (s, g) => (g.doc ? followUps(g.doc) : []),
}

export const mutations = {
  // Closing a file keeps the summaries written so far: reopening it does not ask the AI again.
  RESET(s) { Object.assign(s, closed(), { summaries: s.summaries }) },
  SET(s, patch) { Object.assign(s, patch) },
}

// The one object URL this module owns — the bytes of the file now open. Revoked when another file
// takes its place or the state is put away, never left for the page to leak.
let ownedUrl = null
const release = () => {
  if (ownedUrl) { URL.revokeObjectURL(ownedUrl); ownedUrl = null }
}

/** The record for a document id, from the Recent files page's loaded rows when it holds it. */
function knownRecord(rootState, id) {
  const docs = rootState?.recentFiles?.docs || []
  return docs.find(d => d && String(d.id) === String(id)) || null
}

export const actions = {
  /**
   * OPEN a stored document into the box. `file` is `{ id, name, contentType, isImage }` as
   * lib/attachmentOpen wants it; `document` the record when the caller has it (else it is read from
   * the Recent files page's rows, or fetched); `list` the files the viewer's arrows step through.
   *
   * A file the browser cannot draw (a spreadsheet, a .docx) is downloaded, as it always was
   * (lib/attachmentOpen's rule) — the frames open a PDF, a picture, and (#639) a video or an audio
   * file.
   */
  async open({ commit, dispatch, state: s, rootState }, { file, document = null, list = null } = {}) {
    if (!file?.id) return null
    const url = await documentObjectUrl(api, file)
    const item = attachmentPreviewItem(file, url)
    if (!item) {
      saveObjectUrl(url, file.name || file.fileName)
      URL.revokeObjectURL(url)
      return null
    }
    release()
    ownedUrl = url
    const preview = { ...item, documentId: String(file.id) }
    const rows = Array.isArray(list) ? list : s.list
    commit('SET', {
      status: 'loading',
      viewer: false,
      pages: null,
      openedAt: Date.now(),
      list: rows,
      // The viewer's arrows stay inside the list the file was opened from, and so does its Close.
      openedFrom: rows.some(r => r && String(r.id) === String(file.id)) ? s.openedFrom : null,
      doc: document || knownRecord(rootState, file.id) || { id: String(file.id), filename: file.name || file.fileName || '', contentType: file.contentType || '' },
    })
    dispatch('workboard/openPreview', { item: preview, source: 'timeline' }, { root: true })
    if (!document && !knownRecord(rootState, file.id)) await dispatch('fetchRecord', file.id)
    else commit('SET', { status: 'ready' })
    return preview
  },

  /** The full record, when the caller only had a name and an id. */
  async fetchRecord({ commit, state: s }, id) {
    try {
      const res = await api.getById('documents', id)
      const doc = unwrap(res)
      if (s.doc && String(s.doc.id) === String(id) && doc && doc.id) commit('SET', { doc, status: 'ready' })
      else commit('SET', { status: 'ready' })
    } catch (err) {
      console.error('[file briefing] document failed', err)
      if (s.doc && String(s.doc.id) === String(id)) commit('SET', { status: 'error' })
    }
  },

  /**
   * Put the state away — and the preview with it. A file viewed from the Recent files page goes
   * back to that page (busy-my-web#644): it is reopened as it was left, over the card it was
   * opened over, which is still there underneath.
   */
  close({ commit, dispatch, state: s }) {
    const from = s.openedFrom
    release()
    commit('RESET')
    dispatch('workboard/closePreview', null, { root: true })
    if (from?.from === 'recent-files') return dispatch('recentFiles/open', { restore: from.page }, { root: true })
    return undefined
  },

  /** Where the file now opening was opened from (see the note at the top); null forgets it. */
  markOpenedFrom({ commit }, v) {
    commit('SET', { openedFrom: v && v.from === 'recent-files' ? { from: 'recent-files', page: v.page || null } : null })
  },

  /** The preview was closed elsewhere (the gutter's Close, the Home launcher): forget the file. */
  reset({ commit }) {
    release()
    commit('RESET')
  },

  /**
   * ADOPT the document now in the preview slot when it got there by one of the app's existing
   * roads (an entity's attachment, a timeline row, a Work-board chip) rather than by `open`: the
   * box dispatches this as it mounts and whenever the preview changes. The record comes from the
   * list the viewer steps through, then the Recent files page's rows, then the BFF.
   */
  async adopt({ commit, dispatch, state: s, rootState }, item) {
    const it = item?.item || item
    if (!isFileBriefing(it)) return
    const id = String(it.documentId)
    if (s.doc && String(s.doc.id) === id) return
    const listed = s.list.find(r => r && String(r.id) === id)
    const known = listed || knownRecord(rootState, id)
    // A file from outside the list it was opened with came by another road: its Close is that
    // road's, not the Recent files page's (busy-my-web#644).
    const openedFrom = listed ? s.openedFrom : null
    commit('SET', { doc: known || stubOf(it), tab: 'details', viewer: false, pages: null, openedAt: Date.now(), status: known ? 'ready' : 'loading', openedFrom })
    if (!known) await dispatch('fetchRecord', id)
  },

  /** The files the viewer's arrows step through — records, in the order they are listed. */
  setList({ commit }, list) { commit('SET', { list: Array.isArray(list) ? list.filter(r => r && r.id != null) : [] }) },

  setTab({ commit, dispatch }, tab) {
    const summary = tab === 'summary'
    commit('SET', { tab: summary ? 'summary' : 'details' })
    return summary ? dispatch('loadSummary') : undefined
  },
  /**
   * The Summary tab (frame 61): busy-main-bff `v1/intelligent/document-summary` has the AI write an
   * overview and the key facts from the file's extracted text — asked once per file, on the first
   * visit to the tab, and filed nowhere (it is not a turn of the file's conversation). 204 = the
   * file has no text to summarise. A failed try can be retried from the tab.
   */
  async loadSummary({ state: s, commit, getters: g }, { retry = false } = {}) {
    const id = g.documentId
    if (!id) return
    const known = s.summaries[id]
    if (known && (known.status === 'loading' || (known.status !== 'error' && !retry) || (known.status === 'ready' && retry))) return
    const put = (entry) => commit('SET', { summaries: { ...s.summaries, [id]: entry } })
    put({ status: 'loading', overview: '', facts: [] })
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const data = unwrap(await api.insert('intelligent/document-summary', { documentId: id, timeZoneId }))
      const facts = (data?.keyInformation || [])
        .filter(f => f && f.label && f.value)
        .map(f => ({ label: String(f.label), value: String(f.value) }))
      const overview = String(data?.overview || '').trim()
      put(overview || facts.length ? { status: 'ready', overview, facts } : { status: 'none', overview: '', facts: [] })
    } catch {
      put({ status: 'error', overview: '', facts: [] })
    }
  },
  setPages({ commit }, n) { commit('SET', { pages: Number(n) > 0 ? Number(n) : null }) },
  openViewer({ commit, getters: g }) { if (g.doc) commit('SET', { viewer: true }) },
  closeViewer({ commit }) { commit('SET', { viewer: false }) },

  /** The viewer's arrows: the previous / next file in the list, opened in its place. */
  async step({ state: s, getters: g, dispatch }, d) {
    const next = siblingOf(viewableDocuments(s.list), g.doc?.id, d)
    if (!next) return null
    const viewer = s.viewer
    // The list holds records (the page's rows, a board's documents): the sibling IS its record.
    const opened = await dispatch('open', { file: openableDocument(next), document: next })
    if (opened && viewer) dispatch('openViewer')
    return opened
  },

  /** Select / Selected (frame 63): the file on the Work-board, or off it again. */
  toggleSelect({ getters: g, dispatch }) {
    const chip = fileChip(g.doc)
    if (chip) dispatch('workboard/toggle', chip, { root: true })
  },

  /** The rail's "Clear selection": the file off the board (the briefing stays open). */
  clearSelection({ getters: g, dispatch }) {
    if (g.doc?.id) dispatch('workboard/remove', String(g.doc.id), { root: true })
  },

  /** A question about the open file — typed, sent, or a follow-up clicked. See the hand-off note. */
  ask({ commit, getters: g }, question) {
    const q = String(question || '').trim()
    if (!q || !g.doc) return null
    const record = { question: q, documentId: String(g.doc.id || ''), at: Date.now() }
    commit('SET', { lastQuestion: record })
    return record
  },

  /** Download the open file's bytes under its name — from the URL already in hand. */
  download({ getters: g, rootGetters }) {
    const preview = rootGetters['workboard/preview']
    if (!preview?.url || !g.doc) return
    saveObjectUrl(preview.url, g.doc.filename || preview.title)
  },

  /** The viewer's trash (frame 64): the document gone from Busy, the state put away. */
  async remove({ getters: g, dispatch }) {
    const id = g.doc?.id
    if (!id) return false
    try {
      await api.delete('documents', id)
    } catch (err) {
      console.error('[file briefing] delete failed', err)
      return false
    }
    dispatch('workboard/remove', String(id), { root: true })
    dispatch('close')
    return true
  },
}
